import React from 'react';
import type { NewsItem as NewsItemType } from '../types/news';
import { formatDate } from '../utils/dateUtils';

interface NewsDetailProps {
  news: NewsItemType;
  onBack: () => void;
}

export const NewsDetail: React.FC<NewsDetailProps> = ({ news, onBack }) => {
  const handleExternalLink = () => {
    window.open(news.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <article className="news-detail max-w-3xl mx-auto">
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <header className="bg-gray-50 border-b border-gray-200 px-5 py-4 md:px-8 md:py-5">
          <button
            onClick={onBack}
            className="mb-3 flex items-center text-blue-700 hover:text-blue-900 transition-colors font-medium text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
          >
            <span className="mr-2">←</span>
            Back to News
          </button>

          <div className="flex items-center gap-3 mb-2">
            <span className="inline-block px-3 py-1 bg-red-700 text-white text-xs font-bold uppercase tracking-wider rounded">
              {news.source}
            </span>
            <time dateTime={news.publishedDate} className="text-xs text-gray-500">
              {formatDate(news.publishedDate)}
            </time>
            {news.author && (
              <span className="text-xs text-gray-500">· By {news.author}</span>
            )}
          </div>

          <h1 className="text-xl md:text-2xl font-bold text-gray-900 leading-snug">
            {news.title}
          </h1>

          {news.categories && news.categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {news.categories.map((category, index) => (
                <span
                  key={index}
                  className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded border border-blue-200"
                >
                  {category}
                </span>
              ))}
            </div>
          )}
        </header>

        {/* Image */}
        {news.image && (
          <div className="px-5 md:px-8 pt-4">
            <img
              src={news.image}
              alt={news.title}
              className="w-full h-auto max-h-72 object-cover rounded-lg border border-gray-200"
            />
          </div>
        )}

        {/* Content */}
        <div className="px-5 py-4 md:px-8 md:py-5">
          {news.summary && (
            <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-700 rounded-r-lg">
              <p className="text-sm text-blue-800 leading-relaxed">{news.summary}</p>
            </div>
          )}

          <div className="text-gray-800 leading-relaxed text-sm">
            {news.content ? (
              <div
                className="prose-content"
                dangerouslySetInnerHTML={{ __html: news.content }}
              />
            ) : (
              <div className="bg-gray-100 border border-gray-200 rounded-lg p-5 text-center">
                <p className="text-gray-600 text-sm mb-1">Full content is not available for this article.</p>
                <p className="text-gray-500 text-xs">Please read the original article using the link below.</p>
              </div>
            )}
          </div>

          {/* External link */}
          <div className="mt-5 pt-4 border-t border-gray-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <p className="text-xs text-gray-500">Continue reading on the original source</p>
            <button
              onClick={handleExternalLink}
              className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Read Original Article →
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};
